import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIME = "#ff5a1f"; // TORQ signal orange — house accent, not borrowed from any reference site

const PARTNERSHIP_TYPES = [
  "Title / Presenting Sponsor",
  "Category Sponsor",
  "Product Partnership / Sampling",
  "Venue Partner",
  "Media / Content Partner",
  "Equipment Partner",
  "Hydration / Nutrition Partner",
  "Community / Fitness Partner",
  "Technology / Registration Partner",
  "Merchandise Partner",
  "Other",
];

const BUDGETS = [
  "₹2–5 lakh",
  "₹5–15 lakh",
  "₹15–50 lakh",
  "₹50 lakh–₹1 crore",
  "₹1 crore+",
  "Not sure yet",
  "Prefer not to disclose",
];

const BENEFITS = [
  "Brand visibility across approved event touchpoints.",
  "On-ground activations and product sampling opportunities.",
  "Category partnerships and potential exclusivity.",
  "Digital content and event promotion opportunities.",
  "Connections with athletes, participants, spectators, and fitness communities.",
];

const EMPTY = {
  company: "", contact_name: "", designation: "", email: "", phone: "",
  website: "", partnership_type: "", budget: "", proposal: "", consent: false,
};

export default function PartnersPage() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const missing = [];
    if (form.company.trim().length < 2) missing.push("company / brand name");
    if (form.contact_name.trim().length < 2) missing.push("contact person");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) missing.push("a valid work email");
    if (!form.partnership_type) missing.push("a partnership type");
    if (!form.consent) missing.push("privacy consent");
    if (missing.length) {
      const msg = `Before sending, add: ${missing.join(", ")}.`;
      setError(msg);
      toast.error(msg);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/partners/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not send your enquiry");
      setDone(data);
      toast.success("Enquiry sent — our team will be in touch.");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const darkLabel = "font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2";
  const darkField =
    "w-full bg-white border border-[#d8d8d0] text-[#161614] font-body text-base px-4 py-3 min-h-[48px] rounded-lg outline-none focus:border-[#161614] transition-colors placeholder:text-[#a3a39f]";

  return (
    <main className="min-h-screen bg-[#0c0c0a] text-[#f0eee4] font-body antialiased" data-testid="partners-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="partners-back" className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={14} /> BACK
          </Link>
          <img src="/logo-long.png" alt="TORQ Circuit" className="h-6 w-auto" />
        </div>

        <div className="mt-14 lg:mt-20 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em]" style={{ color: LIME }} data-testid="partners-overline">
              PARTNERSHIPS — SEASON ONE
            </p>
            <h1
              className="mt-6 font-display font-extrabold uppercase leading-[0.88] text-[clamp(3rem,8vw,7rem)]"
              data-testid="partners-heading"
            >
              PARTNER WITH <span style={{ color: LIME }}>TORQ CIRCUIT</span>
            </h1>
            <p className="mt-6 font-display font-bold uppercase text-2xl lg:text-4xl text-[#f0eee4]" data-testid="partners-subheading">
              PUT YOUR BRAND IN THE ACTION.
            </p>
            <p className="mt-6 max-w-xl text-[#a3a39f] leading-relaxed text-base lg:text-lg" data-testid="partners-intro">
              Connect your brand with a community driven by fitness, performance, competition, and an active
              lifestyle. Explore strategic partnership opportunities with TORQ CIRCUIT and help us build
              memorable fitness experiences.
            </p>

            <ul className="mt-10 space-y-4" data-testid="partners-benefits">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-4 text-sm lg:text-base text-[#a3a39f] leading-relaxed">
                  <span className="mt-1 shrink-0 w-4 h-4 flex items-center justify-center" style={{ color: LIME }}>
                    <Check size={14} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-10 border p-6 max-w-xl" style={{ borderColor: `${LIME}55` }} data-testid="partners-budget-note">
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a]">SPONSORSHIP RANGE</p>
              <p className="mt-3 font-display font-extrabold uppercase text-3xl lg:text-4xl" style={{ color: LIME }}>
                ₹2 LAKH — ₹1 CRORE+
              </p>
              <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed">
                TORQ CIRCUIT is open to strategic sponsorships and partnerships in this range, depending on the
                scope of collaboration and mutually agreed deliverables.
              </p>
            </div>

            <button
              onClick={scrollToForm}
              data-testid="partners-cta"
              className="mt-10 inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] px-8 py-4 min-h-[52px] rounded-full font-bold text-[#0c0c0a] transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: LIME }}
            >
              TELL US ABOUT YOUR BRAND <ArrowDown size={14} />
            </button>
          </div>

          <div id="partner-form" className="scroll-mt-8" data-testid="partners-form-panel">
            <div className="bg-[#f7f7f2] text-[#161614] rounded-2xl p-6 sm:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="partner-success">
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-[#161614]">
                    <Check size={24} strokeWidth={3} />
                  </span>
                  <h2 className="mt-6 font-display font-extrabold uppercase text-3xl">ENQUIRY RECEIVED.</h2>
                  <p className="mt-4 text-sm text-[#454540] leading-relaxed max-w-sm mx-auto">
                    Thank you, {form.contact_name.split(" ")[0]}. Our partnerships team will review your enquiry
                    for {form.company} and reach out at {form.email}.
                  </p>
                  <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-[#6e6e6a]">REF — {done.reference}</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="partner-form">
                  <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl leading-[0.9]" data-testid="partner-form-title">
                    LET'S EXPLORE A PARTNERSHIP
                  </h2>
                  <p className="mt-3 text-sm text-[#454540] leading-relaxed">
                    Tell us about your brand and your partnership goals. Our team will review your enquiry and get in touch.
                  </p>

                  <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="partner-company" className={darkLabel}>COMPANY / BRAND NAME *</label>
                      <input id="partner-company" className={darkField} data-testid="partner-company" value={form.company} onChange={(e) => set("company", e.target.value)} maxLength={120} required />
                    </div>
                    <div>
                      <label htmlFor="partner-name" className={darkLabel}>CONTACT PERSON'S NAME *</label>
                      <input id="partner-name" className={darkField} data-testid="partner-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} maxLength={120} required />
                    </div>
                    <div>
                      <label htmlFor="partner-role" className={darkLabel}>DESIGNATION / ROLE</label>
                      <input id="partner-role" className={darkField} data-testid="partner-role" value={form.designation} onChange={(e) => set("designation", e.target.value)} maxLength={120} />
                    </div>
                    <div>
                      <label htmlFor="partner-email" className={darkLabel}>WORK EMAIL *</label>
                      <input id="partner-email" type="email" className={darkField} data-testid="partner-email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
                    </div>
                    <div>
                      <label htmlFor="partner-phone" className={darkLabel}>PHONE / WHATSAPP</label>
                      <input id="partner-phone" type="tel" className={darkField} data-testid="partner-phone" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={20} />
                    </div>
                    <div>
                      <label htmlFor="partner-website" className={darkLabel}>WEBSITE OR INSTAGRAM</label>
                      <input id="partner-website" className={darkField} data-testid="partner-website" placeholder="yoursite.com or @handle" value={form.website} onChange={(e) => set("website", e.target.value)} maxLength={200} />
                    </div>
                    <div>
                      <label htmlFor="partner-type" className={darkLabel}>PARTNERSHIP TYPE *</label>
                      <select id="partner-type" className={darkField} data-testid="partner-type" value={form.partnership_type} onChange={(e) => set("partnership_type", e.target.value)} required>
                        <option value="">SELECT</option>
                        {PARTNERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="partner-budget" className={darkLabel}>INDICATIVE BUDGET</label>
                      <select id="partner-budget" className={darkField} data-testid="partner-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                        <option value="">SELECT (OPTIONAL)</option>
                        {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <p className="mt-3 font-mono text-[10px] tracking-[0.05em] text-[#6e6e6a] leading-relaxed" data-testid="partner-budget-note">
                    Partnership budgets are indicative and depend on the scope of collaboration, brand visibility,
                    event activations, category exclusivity, and mutually agreed deliverables. All proposals are
                    subject to discussion and written agreement.
                  </p>

                  <div className="mt-4">
                    <label htmlFor="partner-proposal" className={darkLabel}>PARTNERSHIP PROPOSAL</label>
                    <textarea
                      id="partner-proposal"
                      className={`${darkField} min-h-[120px]`}
                      data-testid="partner-proposal"
                      placeholder="Tell us about your brand, objectives, products, budget, and the kind of collaboration you are considering."
                      value={form.proposal}
                      onChange={(e) => set("proposal", e.target.value)}
                      maxLength={4000}
                    />
                  </div>

                  <label className="mt-5 flex items-start gap-3 text-sm text-[#454540] leading-relaxed cursor-pointer" data-testid="partner-consent-label">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 shrink-0 accent-[#161614]"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      data-testid="partner-consent"
                    />
                    <span>
                      I agree to be contacted by TORQ CIRCUIT about this enquiry and have read the{" "}
                      <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline underline-offset-2 font-medium">Privacy Policy</a>.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="partner-submit"
                    className="mt-6 w-full rounded-full font-mono text-xs tracking-[0.25em] py-4 min-h-[52px] font-bold text-[#0c0c0a] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
                    style={{ background: LIME }}
                  >
                    {busy ? (
                      <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> SENDING…</span>
                    ) : (
                      "SEND PARTNERSHIP ENQUIRY"
                    )}
                  </button>

                  {error && (
                    <p className="mt-4 font-mono text-[11px] tracking-[0.1em] text-[#8c2f2f] border border-[#8c2f2f]/40 rounded-lg px-4 py-3" data-testid="partner-error">
                      {error}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
